import { Link } from 'react-router-dom'
import { CheckIcon } from '../components/icons'
import { WIZARD_STEPS } from './steps'

export type StepStatus = 'Complete' | 'In progress' | 'Needs attention' | 'Not started'

export interface StepperItem {
  index: number
  label: string
  path: string
  status: StepStatus
  reachable: boolean
}

/**
 * Markup contract from DESIGN.md §5.4 — do not deviate:
 *
 *  - completed and current steps are real `<a>`, because **backwards
 *    navigation is always free**
 *  - steps beyond the furthest reached are `<span role="link"
 *    aria-disabled="true">`, never a disabled `<a>` (there is no such thing,
 *    and a focusable disabled link is a keyboard-trap risk)
 *  - the visible `.stepper__hint` is what makes state non-colour-dependent,
 *    and it is what a screen reader reads. It is never hidden from AT.
 *
 * This is navigation, not a widget: plain Tab and Enter, no roving tabindex,
 * no arrow-key handling.
 */
export function Stepper({ items, rail }: { items: StepperItem[]; rail?: boolean }) {
  const current = items.find((item) => item.status === 'In progress')

  return (
    <>
      {rail ? (
        <p className="stepper__progress">
          Step {current?.index ?? 1} of {WIZARD_STEPS.length}
        </p>
      ) : null}
      <nav className={rail ? 'stepper stepper--rail' : 'stepper'} aria-label="Living will progress">
        <ol className="stepper__list" role="list">
          {items.map((item) => {
            const isCurrent = item.status === 'In progress'
            const itemClass = [
              'stepper__item',
              item.status === 'Complete' ? 'is-complete' : '',
              item.status === 'Needs attention' ? 'has-error' : '',
            ]
              .filter(Boolean)
              .join(' ')

            const inner = (
              <>
                <span className="stepper__marker" aria-hidden="true">
                  {item.status === 'Complete' ? <CheckIcon /> : item.index}
                </span>
                <span>
                  <span className="stepper__label">{item.label}</span>
                  <span className="stepper__hint">{item.status}</span>
                </span>
              </>
            )

            return (
              <li className={itemClass} key={item.index}>
                {item.reachable ? (
                  <Link
                    className="stepper__step"
                    to={item.path}
                    {...(isCurrent ? { 'aria-current': 'step' as const } : {})}
                  >
                    {inner}
                  </Link>
                ) : (
                  <span className="stepper__step" role="link" aria-disabled="true">
                    {inner}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </>
  )
}
